<h2 class="section-title">{{ 'components.settingsForm.companySection.title' | translate }}</h2>
@if (isInit) {
    <form [formGroup]="organizationForm" (ngSubmit)="onSubmit()">
        <div class="card form-grid form-grid--2">
            <div class="form-field form-field--wide">
                <label for="organizationLogoInput" class="form-field__label">
                    {{ 'components.settingsForm.companySection.companyLogo.label' | translate }}
                </label>
                <app-image-selector
                    inputId="organizationLogoInput"
                    accept="image/png, image/jpeg"
                    [maxFileSize]="5 * 1024 * 1024"
                    [label]="'components.settingsForm.button.imageUpload' | translate"
                    formControlName="companyLogo"
                    styleClass="flex align-items-center"
                    [placeholder]="userClaim.organization?.name?.substring(0, 2)"></app-image-selector>
                @if (isOrgOwner) {
                    <small class="form-field__help">
                        {{ 'components.settingsForm.companySection.companyLogo.accept' | translate }}
                    </small>
                }
                @if (organizationForm.get('companyLogo')?.invalid && organizationForm.get('companyLogo')?.touched) {
                    @if (organizationForm.get('companyLogo')?.errors?.invalidFileType) {
                        <small class="p-error">{{
                            'components.settingsForm.companySection.toasts.avatarUpload.invalideTypeFileError'
                                | translate
                        }}</small>
                    }
                    @if (organizationForm.get('companyLogo')?.errors?.fileSizeExceeded) {
                        <small class="p-error">{{
                            'components.settingsForm.companySection.toasts.avatarUpload.maxFileSizeError' | translate
                        }}</small>
                    }
                }
            </div>
            <div class="form-field form-field--wide">
                <label for="companyNameInput" class="form-field__label form-field__label--required">
                    {{ 'components.settingsForm.companySection.companyName.label' | translate }}
                </label>
                <input
                    id="companyNameInput"
                    pInputText
                    type="text"
                    formControlName="companyName"
                    [placeholder]="'components.settingsForm.companySection.companyName.placeholder' | translate" />
                @if (
                    organizationForm.get('companyName')?.errors?.required &&
                    organizationForm.get('companyName')?.touched
                ) {
                    <small class="p-error">{{ 'common.forms.validationMessages.required' | translate }}</small>
                }
            </div>
            <div class="form-field">
                <label for="countryInput" class="form-field__label">
                    {{ 'components.settingsForm.entitySection.edit.country.label' | translate }}
                </label>
                <p-select
                    inputId="countryInput"
                    [options]="countries"
                    formControlName="country"
                    [placeholder]="'components.settingsForm.entitySection.edit.country.placeholder' | translate"
                    [filter]="true"
                    filterMatchMode="startsWith"
                    optionLabel="name"
                    optionValue="code"
                    [emptyFilterMessage]="'common.filter.empty' | translate"
                    [virtualScroll]="true"
                    [virtualScrollItemSize]="38"
                    (onChange)="onCountryChange($event)"
                    [filterPlaceholder]="'common.search' | translate">
                </p-select>
            </div>
            <div class="form-field">
                <label for="vatNumberInput" class="form-field__label">
                    {{ 'components.settingsForm.companySection.vatNumber.label' | translate }}
                    <span
                        class="material-symbols-outlined icon-sm align-middle cursor-help"
                        [pTooltip]="'home.onboarding.organiztionSection.vatNumber.toolTip' | translate"
                        >info</span
                    >
                </label>
                <input
                    id="vatNumberInput"
                    pInputText
                    type="text"
                    [placeholder]="vatPlaceholder"
                    formControlName="vatNumber"
                    (blur)="onVatBlur()" />
                @if (
                    organizationForm.get('vatNumber')?.errors?.invalidVat && organizationForm.get('vatNumber')?.touched
                ) {
                    <small class="p-error">{{
                        'components.settingsForm.companySection.vatNumber.invalidVat'
                            | translate: { vatFormat: vatPlaceholder }
                    }}</small>
                }
            </div>
            <div class="form-field form-field--wide">
                <label for="industryInput" class="form-field__label form-field__label--required">
                    {{ 'components.settingsForm.companySection.industryCode.label' | translate }}
                </label>
                <p-select
                    inputId="industryInput"
                    [options]="industries"
                    formControlName="industryCode"
                    [placeholder]="'components.settingsForm.companySection.industryCode.placeholder' | translate"
                    [showClear]="true"
                    [filter]="true"
                    filterBy="industrySectorId,label"
                    [resetFilterOnHide]="true"
                    optionValue="industrySectorId"
                    [emptyFilterMessage]="'common.filter.empty' | translate"
                    [appendTo]="'body'"
                    [overlayOptions]="{ autoZIndex: true, baseZIndex: 1000 }"
                    [filterPlaceholder]="'common.search' | translate">
                    <ng-template let-sa pTemplate="selectedItem">
                        {{ sa.label.length > 85 ? sa.label.slice(0, 85) + '...' : sa.label }}
                    </ng-template>
                    <ng-template let-activity pTemplate="item">
                        <div>
                            {{ activity.label.length > 85 ? activity.label.slice(0, 85) + '...' : activity.label }}
                        </div>
                    </ng-template>
                </p-select>
                @if (
                    organizationForm.get('industryCode')?.errors?.required &&
                    organizationForm.get('industryCode')?.touched
                ) {
                    <small class="p-error">{{ 'common.forms.validationMessages.required' | translate }}</small>
                }
            </div>
            <div class="form-field form-field--wide">
                <label for="naceCodeInput" class="form-field__label">
                    {{ 'components.settingsForm.companySection.naceCode.label' | translate }}
                </label>
                <p-select
                    inputId="naceCodeInput"
                    [options]="groupedNace"
                    formControlName="naceCode"
                    [placeholder]="'components.settingsForm.companySection.naceCode.placeholder' | translate"
                    [showClear]="true"
                    [filter]="true"
                    filterBy="id,label"
                    [resetFilterOnHide]="true"
                    optionValue="id"
                    [emptyFilterMessage]="'common.filter.empty' | translate"
                    [group]="true"
                    [appendTo]="'body'"
                    [overlayOptions]="{ autoZIndex: true, baseZIndex: 1000 }"
                    [filterPlaceholder]="'common.search' | translate">
                    <ng-template let-sa pTemplate="selectedItem">
                        <div class="truncate-text">{{ sa.id }} ({{ sa.label }})</div>
                    </ng-template>
                    <ng-template let-activity pTemplate="item">
                        <div>{{ activity.id }} ({{ activity.label }})</div>
                    </ng-template>
                </p-select>
            </div>
            @if (false) {
                <div class="form-field">
                    <label for="fteInput" class="form-field__label">
                        {{ 'components.settingsForm.companySection.fte.label' | translate }}
                    </label>
                    <p-inputNumber
                        inputId="fteInput"
                        formControlName="fte"
                        [placeholder]="'components.settingsForm.companySection.fte.placeholder' | translate"
                        [min]="1"></p-inputNumber>
                </div>
            }
        </div>
        @if (isOrgOwner) {
            <div class="form-actions">
                <p-button
                    type="submit"
                    [label]="'components.settingsForm.button.settings' | translate"
                    [loading]="loading"
                    data-testid="settings-form-organization-submit-button" />
            </div>
        }
    </form>
}
